import "../../css/ProfilePage/ProfilePage.css";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  AccountData,
  Event,
  Follower,
  NotificationType,
  ProfileContactPageId,
  ProfileHomePageId,
  ProfilePage,
  ProfilePageSection,
  Visibility,
} from "@markit/common.types";
import { isDesktop as isDesktopDevice } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { LoginState, getAccountState } from "../../redux/slices/accountSlice";
import ProfilePageHeader from "./ProfilePageHeader";
import CreatorTextManual from "../CreatorTextManual";
import { Colors } from "../../utils/colors";
import FlatTabs, { FlatTabItem } from "../Tabs/FlatTabs";
import Skeleton from "react-loading-skeleton";
import { Icon } from "@iconify/react";
import ProfileManagePagesModal from "../CreatorDashboard/CreatorProfile/ProfileManagePagesModal";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { API } from "../../API";
import { getUserFollowerRef } from "../../utils/FirebaseUtils";
import { onSnapshot } from "../../firebase";
import RectangleButton from "../Buttons/RectangleButton";
import EmptyStateButton from "../Buttons/EmptyStateButton";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import ProfilePageSubscriber from "./ProfilePageSubscriber";
import ProfilePageAddSectionPanel from "./ProfilePageAddSectionPanel";
import ProfileEventTimelineCardItem from "../DisplayItem/ProfilePage/ProfileEventTimelineCardItem";
import ProfilePageTimelineSection from "./ProfilePageTimelineSection";
import ProfileSectionCardItem from "../DisplayItem/ProfilePage/ProfileSectionCardItem";
import DragDropList from "../Lists/DragDropList";
import { showNotificationBanner } from "../../utils/notificationUtils";
import ProfilePageHideProfileModal from "./ProfilePageHideProfileModal";
import DragDropListIcon from "../Lists/DragDropListIcon";
import { useLocation } from "react-router-dom";

export const ProfilePageSections = Object.values(ProfilePageSection);

type ProfilePageFullProps = {
  userData: AccountData;
  loadingEvents: boolean;
  userAttendingEvents: Event[];
  userEvents: Event[];
  eventOrganizers: AccountData[];
  tabItem: string;
  setTabItem: (tabItem: string) => void;
  profilePages: ProfilePage[];
  setProfilePages: (profilePages: ProfilePage[]) => void;
  forceMobile?: boolean;
};

const ProfilePageFull = memo(function ProfilePageFullFn(
  props: ProfilePageFullProps
) {
  const {
    userData,
    loadingEvents,
    userEvents,
    userAttendingEvents,
    eventOrganizers,
    tabItem,
    setTabItem,
    profilePages,
    setProfilePages,
    forceMobile,
  } = props;
  const { account } = useSelector(getAccountState);
  const { accountData, loggedIn } = account;
  const pathname = useLocation().pathname;
  const dispatch = useDispatch();
  const [loadingFollower, setLoadingFollower] = useState(true);
  const [followerData, setFollowerData] = useState<Follower>();
  const [followingAccountDataFollowing, setFollowingAccountDataFollowing] =
    useState<AccountData[]>([]);
  const [managePagesModal, setManagePagesModal] = useState(false);
  const [isAddPage, setIsAddPage] = useState(false);
  const [addSectionModal, setAddSectionModal] = useState(false);
  const [profilePassword, setProfilePassword] = useState("");

  const styles = {
    mobileScale: forceMobile
      ? {
          transform: "scale(0.9)",
        }
      : {},
  };

  const isDesktop = useMemo(
    () => isDesktopDevice && !forceMobile,
    [forceMobile]
  );

  const isMyAccount = useMemo(
    () =>
      loggedIn === LoginState.LOGGED_IN &&
      accountData.uid === userData?.uid &&
      !forceMobile,
    [accountData.uid, loggedIn, userData?.uid, forceMobile]
  );

  const isMyAccountCreatorMode = useMemo(
    () =>
      isDesktop &&
      isMyAccount &&
      accountData.inCreatorMode &&
      !pathname.includes("/u/"),
    [accountData.inCreatorMode, isDesktop, isMyAccount, pathname]
  );

  const isMyAccountSubscriber = useMemo(
    () =>
      isMyAccount &&
      !accountData.inCreatorMode &&
      accountData.phoneNumber !== "",
    [accountData.inCreatorMode, accountData.phoneNumber, isMyAccount]
  );

  const noProfilePages = useMemo(
    () =>
      (!accountData.inCreatorMode || forceMobile) &&
      (profilePages.length === 0 ||
        profilePages.every((page) => !page.sections.length)),
    [accountData.inCreatorMode, forceMobile, profilePages]
  );

  const currentProfilePage = useMemo(
    () => profilePages.find((page) => page.id === tabItem),
    [profilePages, tabItem]
  );

  const currentPageSections = useMemo(
    () => profilePages.find((page) => page.id === tabItem)?.sections || [],
    [profilePages, tabItem]
  );

  const hidePasswordProfile = useMemo(
    () =>
      !isMyAccount &&
      userData &&
      userData.profilePasswordProtect &&
      profilePassword !== userData.profilePasswordProtect &&
      tabItem !== ProfileContactPageId,
    [isMyAccount, userData, profilePassword, tabItem]
  );

  const ProfileTabNames: FlatTabItem[] = useMemo(() => {
    const tabs: FlatTabItem[] = [];
    if (!noProfilePages) {
      for (let i = 0; i < profilePages.length; i++) {
        tabs.push({
          id: profilePages[i].id,
          label: profilePages[i].title,
        });
      }
    }
    if (isMyAccountSubscriber) {
      tabs.push(
        { id: ProfileHomePageId, label: "Home" },
        { id: "subscriptions", label: "Subscriptions" }
      );
    }

    return tabs;
  }, [isMyAccountSubscriber, noProfilePages, profilePages]);

  // Reset the tab value if you navigate to another profile page
  useEffect(() => {
    setTabItem(ProfileHomePageId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Will reset when the path changes

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    // Fetch follower data if viewing someone else's profile
    if (!userData || loggedIn !== LoginState.LOGGED_IN) {
      return;
    }

    const { followingAccounts } = await API.user.followingUsers({
      uid: userData.uid,
    });
    setFollowingAccountDataFollowing(followingAccounts);

    // Check if viewing own profile
    if (accountData.uid === userData.uid) {
      setLoadingFollower(false);
      return;
    }

    const followerRef = getUserFollowerRef(userData.uid, accountData.uid);
    const unsubscribe = onSnapshot(followerRef, async (snapshot) => {
      const followerData = snapshot.data();
      if (followerData) {
        setFollowerData(followerData);
      }
      setLoadingFollower(false);
    });
    return () => {
      unsubscribe();
    };
  }, [
    accountData.inCreatorMode,
    accountData.uid,
    isMyAccount,
    loggedIn,
    userData,
  ]);

  const handleOnDrag = useCallback(
    async (newSections: ProfilePageSection[]) => {
      if (!currentProfilePage) return;
      const updatedProfilePage: ProfilePage = {
        ...currentProfilePage,
        sections: newSections as ProfilePageSection[],
      };
      setProfilePages(
        profilePages.map((page) =>
          page.id === currentProfilePage.id ? updatedProfilePage : page
        )
      );
      await API.profile.updateProfilePage({
        uid: accountData.uid,
        profilePage: updatedProfilePage,
      });
    },
    [setProfilePages, currentProfilePage, profilePages, accountData.uid]
  );

  const removeSectionOnPress = useCallback(
    (section: string) => {
      const newSections = currentPageSections.filter((s) => s !== section);
      handleOnDrag(newSections as ProfilePageSection[]);
      showNotificationBanner(
        dispatch,
        "Section Removed",
        NotificationType.NEGATIVE
      );
    },
    [currentPageSections, dispatch, handleOnDrag]
  );

  const SkeletonLoading = useMemo(
    () => (
      <div className="ColumnNormal" style={{ gap: 10 }}>
        {[1, 2, 3].map((_, index) => (
          <Skeleton
            key={index}
            height={110}
            baseColor={Colors.WHITE1}
            borderRadius={20}
          />
        ))}
      </div>
    ),
    []
  );

  const renderEmptyPage = useMemo(
    () =>
      forceMobile ? (
        <EmptyStateButton
          title={"Nothing to see here"}
          description="When you add sections to this page they will show up here"
          icon={
            <Icon
              icon="ion:document-outline"
              height={32}
              style={{ color: Colors.GRAY1 }}
            />
          }
          iconBox={48}
          iconBackgroundColor={Colors.WHITE1}
          containerStyles={{ paddingBlock: 40 }}
        />
      ) : (
        <EmptyStateButton
          title="Customize Link in Bio"
          description="Add links, products, events, forms and more. Then share your link in bio."
          icon={
            <Icon
              icon="mdi:link-variant"
              height={57}
              style={{ color: Colors.BLUE5 }}
            />
          }
          iconBox={80}
          iconBackgroundColor={Colors.WHITE1}
          btnText="Add Section"
          onPress={() => setAddSectionModal(true)}
          containerStyles={{ paddingBlock: 80 }}
        />
      ),
    [forceMobile]
  );

  return (
    <div
      className="ColumnCentering"
      style={{ width: "100%", paddingTop: isDesktop ? 30 : 20 }}
    >
      <div
        className="ColumnCentering"
        style={{
          width: "100%",
          gap: 24,
          transformOrigin: "bottom",
          ...styles.mobileScale,
        }}
      >
        <ProfilePageHeader
          followerData={followerData}
          otherUserData={userData}
          isLoading={loggedIn === LoginState.LOGGED_IN && loadingFollower}
          forceMobile={forceMobile}
        />
        <div style={{ position: "relative", width: "100%" }}>
          <>
            {/* Left fade overlay */}
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 20,
                background:
                  "linear-gradient(to right, rgba(255, 255, 255, 0.9), transparent)",
                zIndex: 2,
                pointerEvents: "none",
              }}
            />
            {/* Right fade overlay */}
            <div
              style={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: 20,
                background:
                  "linear-gradient(to left, rgba(255, 255, 255, 0.9), transparent)",
                zIndex: 2,
                pointerEvents: "none",
              }}
            />
          </>
          <FlatTabs
            items={ProfileTabNames}
            tabItem={tabItem}
            setTabItem={setTabItem}
            leftComp={
              isMyAccountCreatorMode ? (
                <div
                  className="AlignedRowSelect"
                  onClick={() => setManagePagesModal(true)}
                  style={{ gap: 7 }}
                >
                  <Icon
                    icon="ion:menu"
                    height={18}
                    style={{ color: Colors.BLACK }}
                  />
                  <span className="bodyMedium">Manage Pages</span>
                </div>
              ) : null
            }
            rightComp={
              isMyAccountCreatorMode ? (
                <div
                  className="AlignedRowSelect"
                  onClick={() => {
                    setManagePagesModal(true);
                    setIsAddPage(true);
                  }}
                  style={{ gap: 7 }}
                >
                  <Icon
                    icon="ion:add"
                    height={18}
                    style={{ color: Colors.GRAY1 }}
                  />
                  <span className="bodySubtextMedium">Add Page</span>
                </div>
              ) : null
            }
          />
        </div>
      </div>
      <HorizontalDivider
        altMargin={0}
        altColor={Colors.GRAY11}
        altWidth={1}
        containerStyles={{ width: "100%" }}
      />
      <div
        style={{
          position: "relative",
          width: isDesktop ? 544 : "100%",
          transformOrigin: "top",
          ...styles.mobileScale,
        }}
      >
        {hidePasswordProfile ? (
          <ProfilePageHideProfileModal
            userData={userData}
            profilePassword={profilePassword}
            setProfilePassword={setProfilePassword}
            forceMobile={forceMobile}
          />
        ) : null}
        <div
          className="ColumnNormal"
          style={{
            gap: isDesktop ? 30 : 20,
            paddingTop: isDesktop ? 30 : 20,
            paddingBottom: isDesktop ? 60 : 24,
            paddingInline: isDesktopDevice ? 0 : 14, // only on actual mobile
            ...(hidePasswordProfile
              ? { filter: "blur(14px)", pointerEvents: "none" }
              : {}),
          }}
        >
          {loadingEvents ? (
            SkeletonLoading
          ) : (
            <div className="ColumnNormal">
              {isMyAccountSubscriber ? (
                <ProfilePageSubscriber
                  userData={userData}
                  followingAccountDataFollowing={followingAccountDataFollowing}
                  tabItem={tabItem}
                  eventOrganizers={eventOrganizers}
                  userEvents={userEvents}
                  userAttendingEvents={userAttendingEvents}
                  loadingEvents={loadingEvents}
                />
              ) : tabItem === ProfileContactPageId || noProfilePages ? (
                <div
                  className="PageDesktop"
                  style={{
                    border: `1px solid ${Colors.GRAY11}`,
                    backgroundColor: Colors.WHITE1,
                    padding: "24px 14px",
                  }}
                >
                  <CreatorTextManual host={userData} />
                </div>
              ) : (
                <div
                  className="ColumnNormal"
                  style={{ gap: 14, minHeight: "30vh" }}
                >
                  {isMyAccountCreatorMode ? (
                    <RectangleButton
                      buttonLabel="Add Section"
                      iconRight={
                        <Icon
                          icon="ion:add-circle"
                          height={20}
                          style={{ color: Colors.WHITE }}
                        />
                      }
                      onPress={() => setAddSectionModal(true)}
                      altColor={Colors.BLACK}
                      altTextColor={Colors.WHITE}
                      containerStyles={{ paddingInline: 30, paddingBlock: 14 }}
                    />
                  ) : null}
                  {currentPageSections.length > 0 ? (
                    <div className="ColumnNormal" style={{ gap: 14 }}>
                      {isMyAccountCreatorMode ? (
                        <DragDropList
                          items={currentPageSections.map((section, index) => ({
                            id: `section-${index}`,
                            value: section,
                          }))}
                          handleChange={(newItems) => {
                            const newSections = newItems.map(
                              (item) => item.value
                            );
                            handleOnDrag(newSections);
                          }}
                        >
                          {(
                            section: { id: string; value: string },
                            _,
                            dragHandleProps: any
                          ) => {
                            const foundEvent = userEvents.find(
                              (event) => event.id === section.value
                            );
                            return (
                              <div className="AlignedRow" style={{ gap: 14 }}>
                                <DragDropListIcon
                                  dragHandleProps={dragHandleProps}
                                />
                                <ProfileSectionCardItem
                                  section={section.value}
                                  renderEventItem={
                                    foundEvent ? (
                                      <ProfileEventTimelineCardItem
                                        event={foundEvent}
                                        organizers={eventOrganizers}
                                        url={`/e/${foundEvent.id}`}
                                        isMyAccount={isMyAccount}
                                        isCreatorView
                                      />
                                    ) : null
                                  }
                                  foundEvent={foundEvent}
                                  removeOnPress={() =>
                                    removeSectionOnPress(section.value)
                                  }
                                />
                              </div>
                            );
                          }}
                        </DragDropList>
                      ) : (
                        currentPageSections.map((section) => {
                          const foundEvent = userEvents.find(
                            (event) => event.id === section
                          );
                          return ProfilePageSections.includes(
                            section as ProfilePageSection
                          ) ? (
                            <ProfilePageTimelineSection
                              userData={userData}
                              profilePageSection={section as ProfilePageSection}
                              eventOrganizers={eventOrganizers}
                              userEvents={userEvents}
                              userAttendingEvents={userAttendingEvents}
                              loadingEvents={loadingEvents}
                            />
                          ) : foundEvent &&
                            foundEvent.visibility === Visibility.Public ? (
                            <div style={{ flex: 1 }}>
                              <ProfileEventTimelineCardItem
                                event={foundEvent}
                                organizers={eventOrganizers}
                                url={`/e/${foundEvent.id}`}
                                isMyAccount={isMyAccount}
                              />
                            </div>
                          ) : null;
                        })
                      )}
                    </div>
                  ) : (
                    renderEmptyPage
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {managePagesModal ? (
        <ProfileManagePagesModal
          setIsVisible={() => {
            setManagePagesModal(false);
            setIsAddPage(false);
          }}
          profilePages={profilePages}
          setProfilePages={setProfilePages}
          isAddPage={isAddPage}
        />
      ) : null}
      {addSectionModal && currentProfilePage ? (
        <ProfilePageAddSectionPanel
          setIsVisible={setAddSectionModal}
          currProfilePage={currentProfilePage}
          profilePages={profilePages}
          setProfilePages={setProfilePages}
          userEvents={userEvents}
        />
      ) : null}
    </div>
  );
});

export default ProfilePageFull;
